import moment from 'moment'
import { twMerge } from 'tailwind-merge'
import React, { useCallback, useEffect, useRef, useState } from 'react'

import { api } from '../services/api.service'

const OptimizedS3FileViewer = ({ fileKey, width = 160, height = 160, quality = 80, lazy = true, className = '', ...rest }) => {
    const [fileUrl, setFileUrl] = useState(null)
    const [isLoading, setIsLoading] = useState(false)
    const [error, setError] = useState(null)
    const [isVisible, setIsVisible] = useState(!lazy)
    const imgRef = useRef(null)
    const observerRef = useRef(null)

    // Intersection Observer for lazy loading
    useEffect(() => {
        if (!lazy) return

        const observer = new IntersectionObserver(
            (entries) => {
                entries.forEach((entry) => {
                    if (entry.isIntersecting && !isVisible) {
                        setIsVisible(true)
                        observer.unobserve(entry.target)
                    }
                })
            },
            {
                rootMargin: '50px', // Start loading 50px before the image enters viewport
            }
        )

        if (imgRef.current) {
            observer.observe(imgRef.current)
            observerRef.current = observer
        }

        return () => {
            if (observerRef.current) {
                observerRef.current.disconnect()
            }
        }
    }, [lazy, isVisible])

    const fetchOptimizedFileUrl = useCallback(
        async (key) => {
            try {
                setIsLoading(true)
                setError(null)

                // Create cache key with dimensions for different sized versions
                const cacheKey = `${key}_${width}x${height}_q${quality}`
                const cachedData = localStorage.getItem(cacheKey)

                if (cachedData) {
                    const { signedUrl, timestamp } = JSON.parse(cachedData)
                    // Cache for 6 hours instead of 12 for more frequent updates
                    if (moment().diff(moment(timestamp), 'hours') < 6) {
                        setFileUrl(signedUrl)
                        setIsLoading(false)
                        return
                    }
                }

                const response = await api(`${process.env.REACT_APP_API_URL}/s3/get-signed-url/${encodeURIComponent(key)}`)

                if (response?.signedUrl) {
                    // Add ImageKit transformations for optimization
                    const optimizedUrl = addImageKitTransformations(response.signedUrl, width, height, quality)

                    // Cache the optimized URL
                    localStorage.setItem(
                        cacheKey,
                        JSON.stringify({
                            signedUrl: optimizedUrl,
                            timestamp: moment().toISOString(),
                        })
                    )

                    setFileUrl(optimizedUrl)
                }
            } catch (err) {
                console.error('Error fetching optimized file URL:', err)
                setError(err.message)
            } finally {
                setIsLoading(false)
            }
        },
        [width, height, quality]
    )

    // Add ImageKit transformations to the URL
    const addImageKitTransformations = (url, w, h, q) => {
        try {
            // Check if it's already an ImageKit URL
            if (url.includes('ik.imagekit.io')) {
                // Add transformations to existing ImageKit URL
                const transformations = `tr=w-${w},h-${h},c-at_max,q-${q},f-auto`
                return url.includes('?') ? `${url}&${transformations}` : `${url}?${transformations}`
            }

            // If it's an S3 URL, we might need to proxy it through ImageKit
            // For now, return the original URL
            return url
        } catch (err) {
            console.error('Error adding ImageKit transformations:', err)
            return url
        }
    }

    useEffect(() => {
        if (fileKey && isVisible) {
            fetchOptimizedFileUrl(fileKey)
        }
    }, [fileKey, isVisible, fetchOptimizedFileUrl])

    // Show loading placeholder
    if (!isVisible || isLoading) {
        return (
            <div ref={imgRef} className={twMerge('bg-gray-200 animate-pulse flex items-center justify-center', className)} style={{ width: `${width}px`, height: `${height}px` }} {...rest}>
                {isLoading && (
                    <svg className='w-6 h-6 text-gray-400' fill='none' stroke='currentColor' viewBox='0 0 24 24'>
                        <path strokeLinecap='round' strokeLinejoin='round' strokeWidth={2} d='M4 16l4.586-4.586a2 2 0 012.828 0L16 16m-2-2l1.586-1.586a2 2 0 012.828 0L20 14m-6-6h.01M6 20h12a2 2 0 002-2V6a2 2 0 00-2-2H6a2 2 0 00-2 2v12a2 2 0 002 2z' />
                    </svg>
                )}
            </div>
        )
    }

    // Show error state
    if (error) {
        return (
            <div className={twMerge('bg-red-50 border border-red-200 flex items-center justify-center text-red-500 text-xs', className)} style={{ width: `${width}px`, height: `${height}px` }} {...rest}>
                Error loading image
            </div>
        )
    }

    // Show image
    return fileUrl ? <img ref={imgRef} src={fileUrl} alt='' loading='lazy' className={twMerge('object-cover', className)} style={{ width: `${width}px`, height: `${height}px` }} onError={() => setError('Failed to load image')} {...rest} /> : null
}

export default OptimizedS3FileViewer
